import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  listAuditLogs,
  listLoginEvents,
  auditStats,
} from "@/lib/audit.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldAlert, Activity, AlertTriangle, KeyRound, Download } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/audit")({
  component: AuditPage,
});

const severityColor: Record<string, string> = {
  info: "secondary",
  notice: "default",
  warning: "default",
  critical: "destructive",
};

function AuditPage() {
  const fetchLogs = useServerFn(listAuditLogs);
  const fetchLogins = useServerFn(listLoginEvents);
  const fetchStats = useServerFn(auditStats);

  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState<string>("all");
  const [loginSearch, setLoginSearch] = useState("");
  const [loginKind, setLoginKind] = useState<string>("all");
  const [onlyFailed, setOnlyFailed] = useState(false);

  const statsQ = useQuery({
    queryKey: ["audit", "stats"],
    queryFn: () => fetchStats(),
  });

  const logsQ = useQuery({
    queryKey: ["audit", "logs", search, severity],
    queryFn: () =>
      fetchLogs({
        data: {
          search: search || undefined,
          severity: severity === "all" ? undefined : (severity as any),
        },
      }),
  });

  const loginsQ = useQuery({
    queryKey: ["audit", "logins", loginSearch, loginKind, onlyFailed],
    queryFn: () =>
      fetchLogins({
        data: {
          search: loginSearch || undefined,
          kind: loginKind === "all" ? undefined : (loginKind as any),
          only_failed: onlyFailed || undefined,
        },
      }),
  });

  const exportCsv = (rows: any[], filename: string) => {
    if (!rows?.length) return;
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        headers
          .map((h) => {
            const v = r[h];
            const s = v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
            return `"${s.replace(/"/g, '""')}"`;
          })
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = statsQ.data;
  const bruteForce = useMemo(() => stats?.bruteForce ?? [], [stats]);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Audit log</h1>
        <p className="text-sm text-muted-foreground">
          Admin actions, login history, and security-sensitive events.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard icon={<Activity className="h-4 w-4" />} label="Events (24h)" value={stats?.audit24 ?? "—"} />
        <StatCard icon={<KeyRound className="h-4 w-4" />} label="Failed logins (24h)" value={stats?.failed24 ?? "—"} />
        <StatCard
          icon={<ShieldAlert className="h-4 w-4" />}
          label="Critical (24h)"
          value={stats?.critical24 ?? "—"}
        />
        <StatCard
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Brute-force flags"
          value={bruteForce.length}
        />
      </div>

      {bruteForce.length > 0 && (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Brute-force suspects (≥5 failures / 24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>IP</TableHead>
                  <TableHead>Failures</TableHead>
                  <TableHead>Targeted emails</TableHead>
                  <TableHead>Last attempt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bruteForce.map((b: any) => (
                  <TableRow key={b.ip}>
                    <TableCell className="font-mono">{b.ip}</TableCell>
                    <TableCell>
                      <Badge variant="destructive">{b.count}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {b.emails.slice(0, 3).join(", ")}
                      {b.emails.length > 3 ? ` +${b.emails.length - 3}` : ""}
                    </TableCell>
                    <TableCell className="text-sm">{new Date(b.last).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="actions">
        <PillTabsList search>
          <PillTabsTrigger value="actions">Admin actions</PillTabsTrigger>
          <PillTabsTrigger value="logins">Login history</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="actions" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-end gap-3 justify-between">
                <div>
                  <CardTitle className="text-base">Action timeline</CardTitle>
                  <CardDescription>Who did what, when, and from where.</CardDescription>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <Input
                    placeholder="Search action, actor, entity…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-64"
                  />
                  <Select value={severity} onValueChange={setSeverity}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Severity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All severities</SelectItem>
                      <SelectItem value="info">Info</SelectItem>
                      <SelectItem value="notice">Notice</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportCsv(logsQ.data ?? [], "audit-logs.csv")}
                    disabled={!logsQ.data?.length}
                  >
                    <Download className="h-4 w-4 mr-1" /> Export
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logsQ.isLoading && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        Loading…
                      </TableCell>
                    </TableRow>
                  )}
                  {!logsQ.isLoading && (logsQ.data ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No events recorded yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {(logsQ.data ?? []).map((r: any) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {new Date(r.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-sm">{r.actor_email ?? r.actor_id ?? "system"}</TableCell>
                      <TableCell className="font-mono text-xs">{r.action}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {r.entity_type ? `${r.entity_type}${r.entity_id ? ` · ${r.entity_id}` : ""}` : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={severityColor[r.severity] as any}>{r.severity}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.ip ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logins" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-end gap-3 justify-between">
                <div>
                  <CardTitle className="text-base">Login history</CardTitle>
                  <CardDescription>Successes, failures, MFA, and lockouts.</CardDescription>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <Input
                    placeholder="Search email, IP, device…"
                    value={loginSearch}
                    onChange={(e) => setLoginSearch(e.target.value)}
                    className="w-64"
                  />
                  <Select value={loginKind} onValueChange={setLoginKind}>
                    <SelectTrigger className="w-44">
                      <SelectValue placeholder="Event" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All events</SelectItem>
                      <SelectItem value="login_success">Login success</SelectItem>
                      <SelectItem value="login_failure">Login failure</SelectItem>
                      <SelectItem value="logout">Logout</SelectItem>
                      <SelectItem value="password_reset">Password reset</SelectItem>
                      <SelectItem value="mfa_challenge">MFA challenge</SelectItem>
                      <SelectItem value="account_locked">Account locked</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Switch id="only-failed" checked={onlyFailed} onCheckedChange={setOnlyFailed} />
                    <Label htmlFor="only-failed" className="text-sm">Only failed</Label>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportCsv(loginsQ.data ?? [], "login-events.csv")}
                    disabled={!loginsQ.data?.length}
                  >
                    <Download className="h-4 w-4 mr-1" /> Export
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Device</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loginsQ.isLoading && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        Loading…
                      </TableCell>
                    </TableRow>
                  )}
                  {!loginsQ.isLoading && (loginsQ.data ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No login events recorded yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {(loginsQ.data ?? []).map((r: any) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {new Date(r.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-sm">{r.email ?? "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{r.kind}</TableCell>
                      <TableCell>
                        {r.success ? (
                          <Badge variant="secondary">success</Badge>
                        ) : (
                          <Badge variant="destructive">{r.failure_reason ?? "failed"}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.ip ?? "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate">
                        {r.device_fingerprint ?? r.user_agent ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: any }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
            <p className="text-2xl font-semibold mt-1">{value}</p>
          </div>
          <div className="text-muted-foreground">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}
